import useGetAPIChatBotKey from '@/hooks/api/useGetAPIChatBotKey';
import { LoginInfoState } from '@/states/loginInfoState';
import { serviceInfoStateContractTypeSelector } from '@/states/serviceInfoState';
import { isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { Constants } from '@/utils/constants';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useRecoilValue } from 'recoil';

/**
 * チャットボット
 * @returns チャットボット
 */
const ChatBot = () => {
  // hooks
  const router = useRouter();
  const contractType = useRecoilValue(serviceInfoStateContractTypeSelector);

  // 非ログイン画面や一部サービス画面では UI 競合を避けるため表示しない。
  const { pathname } = router;
  const excludePathnames = Constants.PathList.NOT_LOGIN.concat(['/service/']);
  if (excludePathnames.some((e) => e.startsWith(pathname))) {
    return null;
  }

  // 契約種別が分からないと適切なチャット窓口を出せないため非表示にする。
  if (!contractType) {
    return null;
  }

  // 固定回線とモバイルで別チャット基盤を使うため、ここで分岐する。
  if (contractType === Constants.ServiceType.HIKARI) {
    return <FixedChatbot />;
  }
  if (contractType === Constants.ServiceType.MOBILE) {
    return <MobileChatbot />;
  }
  return null;
};

export default ChatBot;

/**
 * 「固定回線サービス」のチャット表示のコンポーネント
 * @returns コンポーネント
 */
const FixedChatbot = () => {
  // ログイン情報
  const loginInfo = useRecoilValue(LoginInfoState);
  // マイページチャットの顧客Keyを発行するAPI呼び出しフック
  const { execute: getAPIChatBotKey } = useGetAPIChatBotKey();

  // チャット開始時に顧客キーを払い出し、ログイン中ユーザーとして会話を引き継げるようにする。
  const callAPIChatBotKey = async () => {
    return getAPIChatBotKey()
      .then(async () => {
        const { data } = await getAPIChatBotKey();
        return data.key;
      })
      .catch((error: any) => {
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
        }
        return '';
      });
  };

  // 顧客キー発行にログイン前提があるため、未ログイン時は描画しない。
  if (!loginInfo.userId) return null;

  return (
    <Script
      id='mypage-chatbot'
      data-testid='mypage-chatbot'
      src={`${process.env.NEXT_PUBLIC_FASTCHAT_DOMAIN}nuro/chat/112/js/embed-fastchat.js`}
      type='text/javascript'
      defer
      onLoad={() => {
        // 埋め込みスクリプト読込後に固定回線用チャットを初期化する。
        (window as any).embedFastchat(
          `${process.env.NEXT_PUBLIC_FASTCHAT_DOMAIN}nuro/chat`,
          112,
          '1'
        );

        window.addEventListener(
          'message',
          (event) => {
            const eventType = event.data.type;
            console.log(`###WEB_SITE_LOG### eventType:${eventType}`);
            // 外部チャット側から顧客情報要求が来た時だけ安全に顧客キーを返す。
            // eslint-disable-next-line eqeqeq
            if (eventType == 'requestUserInfo') {
              sendUserInfoAndStartChat(event.source);
            }
          },
          false
        );

        async function sendUserInfoAndStartChat(postTarget: any) {
          console.log('###WEB_SITE_LOG### call sendUserInfoAndStartChat');
          console.log(postTarget);
          const key = await callAPIChatBotKey();
          postTarget.postMessage(
            {
              type: 'sendUserInfoAndStartChat',
              customerInfoJson: {
                key,
              },
            },
            '*'
          );
        }
      }}
    />
  );
};

/**
 * 「モバイルサービス」のチャット表示のコンポーネント
 * @returns コンポーネント
 */
const MobileChatbot = () => {
  const src = `${process.env.NEXT_PUBLIC_MOBILE_CHAT_URL}`;
  return (
    <Script
      src={src}
      type='text/javascript'
      defer
      // モバイル専用チャット基盤を読み込み、固定回線とは別設定で初期化する。
      onLoad={() =>
        (window as any).embedFastchat(
          'https://support.sonynetwork.co.jp/faqsupport/nuromobile-bot/chat',
          150
        )
      }
    />
  );
};
